import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Link2, Unlink, RefreshCw, Download, Activity, Watch } from "lucide-react";
import { formatActivitySummary } from "@/lib/fitnessImport";

const MODALITY_LABELS = {
  running: "Course",
  cycling: "Vélo",
  swimming: "Natation",
  general: "Autre",
};

export default function FitnessImportPanel({
  selectedDay,
  exercises,
  onImportPrefill,
  fitnessQueryStatus,
  onClearFitnessQuery,
}) {
  const [status, setStatus] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingActs, setLoadingActs] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const stravaConnected = status?.strava?.connected;
  const stravaConfigured = status?.strava?.configured !== false;

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/fitness/status");
      setStatus(data);
      return data;
    } catch {
      toast.error("Impossible de charger les connexions fitness");
      return null;
    }
  }, []);

  const loadActivities = useCallback(async () => {
    setLoadingActs(true);
    try {
      const { data } = await api.get("/fitness/strava/activities", { params: { per_page: 15 } });
      setActivities(data.activities || []);
    } catch {
      toast.error("Erreur chargement activités Strava");
      setActivities([]);
    } finally {
      setLoadingActs(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const s = await loadStatus();
      if (s?.strava?.connected) {
        await loadActivities();
      }
      setLoading(false);
    })();
  }, [loadStatus, loadActivities]);

  useEffect(() => {
    if (!fitnessQueryStatus) return;
    if (fitnessQueryStatus === "strava_success") {
      toast.success("Strava connecté !");
      loadStatus().then((s) => s?.strava?.connected && loadActivities());
    } else if (fitnessQueryStatus === "strava_denied") {
      toast.error("Connexion Strava annulée");
    } else if (fitnessQueryStatus === "strava_error") {
      toast.error("Erreur de connexion Strava");
    }
    onClearFitnessQuery?.();
  }, [fitnessQueryStatus, loadStatus, loadActivities, onClearFitnessQuery]);

  const handleConnectStrava = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/fitness/strava/connect");
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("URL Strava indisponible");
    } catch (err) {
      toast.error(err.response?.data?.error || "Strava non disponible");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete("/fitness/strava/disconnect");
      setStatus((prev) => ({
        ...prev,
        strava: { provider: "strava", connected: false, configured: stravaConfigured },
      }));
      setActivities([]);
      toast.success("Strava déconnecté");
    } catch {
      toast.error("Erreur déconnexion");
    }
  };

  const handleImport = async (activity) => {
    if (!selectedDay) {
      toast.error("Sélectionnez d'abord un jour d'entraînement");
      return;
    }
    if (!exercises?.length) {
      toast.error("Chargez le programme du jour avant d'importer");
      return;
    }

    setImportingId(activity.external_id);
    try {
      const { data } = await api.post("/fitness/strava/prefill", {
        external_id: activity.external_id,
        exercises: exercises.map((ex) => ({ modality: ex.modality, sets: ex.sets })),
      });
      onImportPrefill(data.prefill, data.activity);
      toast.success(`« ${activity.name} » importée sur la séance`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Import impossible");
    } finally {
      setImportingId(null);
    }
  };

  if (loading) {
    return (
      <Card className="mt-6 border-orange-200 bg-orange-50/40">
        <CardContent className="p-4">
          <Skeleton className="h-6 w-48 mb-3" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6 border-orange-200 bg-gradient-to-br from-orange-50/80 to-white" data-testid="fitness-import-panel">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-600" />
              <h2 className="font-heading text-lg font-bold text-slate-900">Importer une activité</h2>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Connectez Strava pour préremplir distance, durée et allure sur votre séance du jour.
            </p>
          </div>
          {stravaConnected && (
            <Button variant="outline" size="sm" onClick={loadActivities} disabled={loadingActs}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingActs ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Strava */}
          <div className="flex-1 min-w-[200px] rounded-xl border border-orange-200 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#FC4C02]">Strava</span>
                {stravaConnected ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Connecté</Badge>
                ) : (
                  <Badge variant="secondary">Non connecté</Badge>
                )}
              </div>
              {stravaConnected ? (
                <Button variant="ghost" size="sm" onClick={handleDisconnect} className="text-slate-500">
                  <Unlink className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleConnectStrava}
                  disabled={connecting || !stravaConfigured}
                  className="bg-[#FC4C02] hover:bg-[#e04400] text-white"
                >
                  <Link2 className="w-4 h-4 mr-1" />
                  Connecter
                </Button>
              )}
            </div>
            {stravaConnected && status?.strava?.athlete_name && (
              <p className="text-xs text-slate-500 mt-2">{status.strava.athlete_name}</p>
            )}
            {!stravaConfigured && !stravaConnected && (
              <p className="text-xs text-amber-700 mt-2">Configuration serveur en attente (clés API Strava).</p>
            )}
          </div>

          {/* Garmin — bientôt */}
          <div className="flex-1 min-w-[200px] rounded-xl border border-slate-200 bg-slate-50 p-3 opacity-80">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Watch className="w-4 h-4 text-slate-600" />
                <span className="font-semibold text-slate-700">Garmin</span>
                <Badge variant="outline">Bientôt</Badge>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">{status?.garmin?.message}</p>
          </div>
        </div>

        {stravaConnected && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Activités récentes</p>
            {loadingActs ? (
              <div className="space-y-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : activities.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Aucune activité récente sur Strava.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {activities.map((act) => (
                  <div
                    key={act.external_id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{act.name}</p>
                      <p className="text-xs text-slate-500">
                        {MODALITY_LABELS[act.modality] || act.type} · {formatActivitySummary(act)}
                        {act.session_date ? ` · ${act.session_date}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!selectedDay || importingId === act.external_id}
                      onClick={() => handleImport(act)}
                      className="shrink-0"
                    >
                      {importingId === act.external_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-3.5 h-3.5 mr-1" />
                          Lier
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {!selectedDay && activities.length > 0 && (
              <p className="text-xs text-amber-700">Choisissez un jour du plan pour activer « Lier ».</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}